<template>
  <div
      v-if="props.exercise"
      class="custom-card-exercise p-3 mx-2 my-3 d-flex flex-column"
      :class="{ completed: props.exercise?.status === statusExerciseEnum.COMPLETED }"
      :data-tour="props.isFirst ? 'exercise-card' : undefined"
      @click="navigateToExercise(props.exercise?.id)"
  >
    <div class="top-row d-flex justify-content-between align-items-start mb-2">
      <div
          class="iconContainer d-flex justify-content-center align-items-center"
          :class="{'customColorEngine': props.exercise?.icon === 'electric-motor'}"
      >
        <img
            v-if="iconSvg"
            :src="iconSvg"
            alt="Ícone com a representação do que o exercício aborda"
            width="35"
        >
        <i v-else-if="iconClass" :class="iconClass" class="icon-fallback"></i>
      </div>

      <div v-if="props.exercise?.difficulty" :class="['difficulty-badge-pill', difficultyClass]">
        <i class="bi bi-reception-4 me-1"></i>
        {{ props.exercise?.difficulty }}
      </div>
    </div>

    <div class="mt-2 flex-grow-1 d-flex flex-column">
      <h5 class="custom-tittle-text overflow-hidden mb-3">{{ props.exercise?.title }}</h5>

      <div class="mb-3 d-flex align-items-center flex-wrap gap-2 badges-row" :data-tour="props.isFirst ? 'status-badge' : undefined">
        <BadgeNumberExercise
            class="pill-badge-base"
            :numberExercise="props.exercise?.order"
            :iconName="props.exercise?.icon"
        />

        <div
            v-if="props.exercise?.status === statusExerciseEnum.COMPLETED"
            class="pill-badge-base custom-pill-status d-flex align-items-center"
        >
          <i class="bi bi-check-circle-fill me-1" style="font-size: 0.7rem;"></i>
          CONCLUÍDO
        </div>
      </div>

      <div class="overflow-auto custom-text-card pe-2 flex-grow-1">
        <p class="m-0">{{ props.exercise?.description }}</p>
      </div>

      <div v-if="isTeacherOrAbove" class="mt-auto pt-2">
        <button
          class="btn btn-sm btn-outline-primary w-100"
          @click.stop="showCapabilities = true"
          data-tour="capabilities-button"
        >
          <i class="bi bi-mortarboard me-1"></i>Ver Capacidades por Curso
        </button>
      </div>
    </div>

    <CapabilitiesModal
      v-model="showCapabilities"
      :exerciseId="props.exercise?.id"
      :exerciseTitle="props.exercise?.title"
    />
  </div>
</template>

<script setup>
import BadgeNumberExercise from '@/components/layoutParts/BadgeNumberExercise.vue';
import CapabilitiesModal from '@/components/Modals/CapabilitiesModal.vue';
import { useRouter } from 'vue-router';
import { defineProps, computed, ref } from 'vue';
import { useExerciseStore } from '@/stores/exercise';
import { useAuthStore } from '@/stores/auth';
import statusExerciseEnum from '@/enums/statusExerciseEnum';

const router = useRouter();
const store = useExerciseStore();
const authStore = useAuthStore();

const showCapabilities = ref(false);

const props = defineProps({
  exercise: {
    type: Object,
    required: true
  },
  isFirst: {
    type: Boolean,
    default: false
  }
});

const isTeacherOrAbove = computed(() => {
  const user = authStore.getLoggedInUsernameAndName();
  return user?.profile && user.profile !== 'STUDENT' && user.profile !== 'Estudante';
});

const difficultyClass = computed(() => {
  const level = props.exercise?.difficulty?.toLowerCase();
  if (level === 'iniciante') return 'badge-iniciante';
  if (level === 'intermediário' || level === 'intermediario') return 'badge-intermediario';
  if (level === 'avançado' || level === 'avancado') return 'badge-avancado';
  return 'badge-default';
});

const svgIcons = require.context('@/assets/images/iconsExercises/', false, /\.svg$/);
const svgIconMap = Object.fromEntries(
  svgIcons.keys().map(key => [key.replace('./', '').replace('.svg', ''), svgIcons(key)])
);

const iconSvg = computed(() => {
  const name = props.exercise?.icon;
  if (!name || name.includes(' ')) return null;
  return svgIconMap[name] || null;
});

const iconClass = computed(() => {
  const name = props.exercise?.icon;
  if (!name || !name.includes(' ')) return 'bi bi-journal-text';
  return name;
});

function navigateToExercise(idExercise) {
  store.setCurrentExercise(idExercise);
  router.push({ name: 'Exercise' });
}
</script>

<style scoped>
.custom-card-exercise {
  max-width: 325px;
  min-width: 325px;
  height: 320px;
  border: 2px solid #E8EFFB;
  border-radius: 16px;
  background-color: white;
  cursor: pointer;
  transition: .2s;
}

.completed {
  border: 2px solid #4eca6b !important;
}

.custom-card-exercise:hover {
  background: #f4f7fc;
  transform: scale(1.05);
}

.custom-text-card {
  color: #8C8C8C;
  font-size: 0.9rem;
  line-height: 1.4;
  max-height: 75px;
}

.custom-tittle-text {
  max-height: 50px;
  font-size: 1.1rem;
  font-weight: 700;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.iconContainer {
  min-width: 52px;
  max-width: 52px;
  min-height: 52px;
  background-color: #E8EFFB;
  border-radius: 8px;
}

.customColorEngine {
  background-color: #c7dfe2 !important;
}

.icon-fallback {
  font-size: 1.8rem;
  color: #1A5FD5;
}

.pill-badge-base {
  padding: 4px 14px !important;
  border-radius: 50rem !important;
  font-size: 0.65rem !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.badges-row :deep(.badge),
.badges-row :deep(span),
.badges-row :deep(.custom-badge-pill) {
  padding: 4px 14px !important;
  border-radius: 50rem !important;
  font-size: 0.65rem !important;
  font-weight: 800 !important;
}

.difficulty-badge-pill {
  padding: 4px 14px;
  border-radius: 50rem;
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-iniciante {
  color: #2e7d32;
  background-color: #ffffff;
  border: 1.5px solid #2e7d32;
}

.badge-intermediario {
  color: #ed6c02;
  background-color: #ffffff;
  border: 1.5px solid #ed6c02;
}

.badge-avancado {
  color: #d32f2f;
  background-color: #ffffff;
  border: 1.5px solid #d32f2f;
}

.badge-default {
  color: #6c757d;
  background-color: #ffffff;
  border: 1.5px solid #6c757d;
}

.custom-pill-status {
  background-color: #e6f4ea;
  color: #1e8e3e;
  border: 1.5px solid transparent;
}
</style>